import api from './authService';

export interface DailyRecap {
  id: number;
  employee_id: number;
  recap_date: string;
  problems_resolved?: string;
  sick_injury?: string;
  problems_to_solve?: string;
  new_clients_payment?: string;
  videos_to_watch?: string;
  client_satisfaction?: string;
  daily_remarks?: string;
  not_engaged?: string;
  out_status?: string;
  store_id?: number;
  created_at: string;
  updated_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  position?: string;
  store_name?: string;
  formatted_date?: string;
  is_validated?: boolean | number;
  validated_by?: number;
  validated_at?: string;
}

export interface CreateRecapData {
  recap_id?: number; // Optional: if provided, update this specific recap
  recap_date: string;
  problems_resolved?: string;
  sick_injury?: string;
  problems_to_solve?: string;
  new_clients_payment?: string;
  videos_to_watch?: string;
  client_satisfaction?: string;
  daily_remarks?: string;
  not_engaged?: string;
  out_status?: string;
  store_id?: number;
}

export const recapService = {
  // Create or update daily recap
  async createOrUpdateRecap(recapData: CreateRecapData): Promise<void> {
    try {
      await api.post('/recaps', recapData);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create/update recap');
    }
  },

  // Get recap for specific date
  async getRecapByDate(date: string): Promise<DailyRecap | null> {
    try {
      const response = await api.get(`/recaps/date/${date}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recap');
    }
  },

  // Get all recaps (admin gets all, others get their own) with pagination
  async getAllRecaps(
    startDate?: string, 
    endDate?: string, 
    storeId?: number, 
    is_validated?: boolean | string,
    page: number = 1,
    limit: number = 10
  ): Promise<{ data: DailyRecap[]; pagination: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number } }> {
    try {
      let url = '/recaps';
      const params = new URLSearchParams();
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (storeId) params.append('storeId', storeId.toString());
      if (is_validated !== undefined) params.append('is_validated', String(is_validated));
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      return {
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recaps');
    }
  },

  // Delete a recap
  async deleteRecap(id: number): Promise<void> {
    try {
      await api.delete(`/recaps/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete recap');
    }
  },

  // Validate or unvalidate a recap (admin only)
  async validateRecap(recapId: number, is_validated: boolean): Promise<void> {
    try {
      await api.put(`/recaps/${recapId}/validate`, { is_validated });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to validate recap');
    }
  }
};
